import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import {
  getStageQualificationHints,
  getStageQualificationSchema,
  validateStageQualificationAnswers,
  buildStageQualificationJustificationDraft
} from '../../services/api/implementation/impl';
import {
  buildQuarterHourTimeSlots,
  joinDatetimeLocalValue,
  snapDatetimeLocalValue,
  splitDatetimeLocalValue
} from '../../utils/datetimeQuarterHour';

const AUTO_FILL_SKIP_FIELDS = new Set(['justification']);

function stripAutoSkippedSuggestions(suggestions) {
  if (!suggestions || typeof suggestions !== 'object') return {};
  const out = { ...suggestions };
  for (const key of AUTO_FILL_SKIP_FIELDS) {
    delete out[key];
  }
  return out;
}

const EMPTY = {};

function mergeSuggestions(base, suggestions) {
  if (!suggestions || typeof suggestions !== 'object') return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(stripAutoSkippedSuggestions(suggestions))) {
    if (value == null || value === '') continue;
    const nextValue =
      key === 'next_follow_up_datetime' && typeof value === 'string'
        ? snapDatetimeLocalValue(value)
        : value;
    if (merged[key] == null || merged[key] === '' || (Array.isArray(merged[key]) && !merged[key].length)) {
      merged[key] = nextValue;
    }
  }
  return merged;
}

function FieldInput({
  field,
  value,
  otherValue,
  onChange,
  suggested,
  onBuildJustification,
  buildingJustification
}) {
  const quarterHourTimeSlots = useMemo(() => buildQuarterHourTimeSlots(), []);
  const id = field.id;
  const showHint =
    suggested != null &&
    suggested !== '' &&
    !(Array.isArray(suggested) && !suggested.length) &&
    (value == null || value === '' || (Array.isArray(value) && !value.length));

  if (field.type === 'select') {
    return (
      <div className="flex flex-col gap-1">
        <select
          value={value ?? ''}
          onChange={(e) => onChange(id, e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Select…</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {showHint ? (
          <button
            type="button"
            className="text-left text-xs text-blue-700 hover:underline"
            onClick={() => onChange(id, suggested)}>
            Use AI suggestion: {Array.isArray(suggested) ? suggested.join(', ') : String(suggested)}
          </button>
        ) : null}
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    const suggestedList = Array.isArray(suggested) ? suggested : [];
    const otherKey = `${id}_other`;
    const showOtherInput = selected.includes('other');
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          {(field.options ?? []).map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${
                  checked ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300'
                }`}>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value];
                    onChange(id, next);
                    if (opt.value === 'other' && checked) {
                      onChange(otherKey, '');
                    }
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
        {showOtherInput ? (
          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-2">
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Describe &quot;Other&quot; <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={otherValue ?? ''}
              onChange={(e) => onChange(otherKey, e.target.value)}
              placeholder="e.g. Asked for corner unit with garden view"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : null}
        {showHint && suggestedList.length ? (
          <button
            type="button"
            className="text-left text-xs text-blue-700 hover:underline"
            onClick={() => onChange(id, suggestedList)}>
            Apply AI-detected signals ({suggestedList.length})
          </button>
        ) : null}
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="flex flex-col gap-1">
        <textarea
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(id, e.target.value)}
          placeholder={field.placeholder}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {field.id === 'justification' ? (
          <button
            type="button"
            disabled={buildingJustification}
            className="text-left text-xs font-medium text-teal-700 hover:underline disabled:opacity-50"
            onClick={onBuildJustification}>
            {buildingJustification ? 'Building summary…' : 'Build summary from your answers'}
          </button>
        ) : showHint ? (
          <button
            type="button"
            className="text-left text-xs text-blue-700 hover:underline"
            onClick={() => onChange(id, suggested)}>
            Use AI draft justification
          </button>
        ) : null}
      </div>
    );
  }

  if (field.type === 'datetime' || field.type === 'date') {
    if (field.type === 'date') {
      return (
        <input
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(id, e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }

    const { date, time } = splitDatetimeLocalValue(value);
    const resolvedTime = quarterHourTimeSlots.includes(time) ? time : quarterHourTimeSlots[0];

    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          type="date"
          value={date}
          onChange={(e) => onChange(id, joinDatetimeLocalValue(e.target.value, resolvedTime))}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={resolvedTime}
          onChange={(e) => onChange(id, joinDatetimeLocalValue(date, e.target.value))}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {quarterHourTimeSlots.map((slot) => (
            <option key={slot} value={slot}>
              {slot}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(id, e.target.value)}
        placeholder={field.placeholder}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {showHint ? (
        <button
          type="button"
          className="text-left text-xs text-blue-700 hover:underline"
          onClick={() => onChange(id, suggested)}>
          Use AI suggestion: {String(suggested)}
        </button>
      ) : null}
    </div>
  );
}

export default function StageQualificationModal({
  open,
  onClose,
  transition,
  leadId,
  leadName,
  targetStage,
  onSubmit,
  isSubmitting = false,
  currentPstatus = null,
  requireManagerAck = false
}) {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState(EMPTY);
  const [hints, setHints] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [buildingJustification, setBuildingJustification] = useState(false);
  const [managerAcknowledged, setManagerAcknowledged] = useState(false);

  useEffect(() => {
    if (!open || !transition) return;
    let cancelled = false;
    setOptionalOpen(false);
    setManagerAcknowledged(false);
    (async () => {
      try {
        setLoading(true);
        setValidationErrors([]);
        const [schemaData, hintsData] = await Promise.all([
          getStageQualificationSchema(transition, leadId),
          leadId ? getStageQualificationHints(leadId, transition).catch(() => null) : null
        ]);
        if (!cancelled) {
          const requirementOptions =
            hintsData?.requirementOptions ?? schemaData?.requirementOptions;
          let mandatory = schemaData?.mandatory ?? [];
          if (requirementOptions?.length) {
            mandatory = mandatory.map((field) =>
              field.id === 'requirement' ? { ...field, options: requirementOptions } : field
            );
          }
          setSchema({ ...schemaData, mandatory });
          setHints(hintsData);
          setAnswers(
            hintsData?.suggestions
              ? Object.fromEntries(
                  Object.entries(stripAutoSkippedSuggestions(hintsData.suggestions)).map(
                    ([key, val]) => [
                      key,
                      key === 'next_follow_up_datetime' && typeof val === 'string'
                        ? snapDatetimeLocalValue(val)
                        : val
                    ]
                  )
                )
              : EMPTY
          );
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to load qualification form');
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, transition, leadId, onClose]);

  const visibleFields = useMemo(() => {
    if (!schema?.mandatory) return [];
    return schema.mandatory.filter((field) => {
      if (field.id === 'budget_not_discussed_reason') {
        return answers.budget_discussed === 'not_discussed';
      }
      return true;
    });
  }, [schema, answers]);

  const optionalFields = useMemo(() => schema?.optional ?? [], [schema]);

  const showManagerAck =
    requireManagerAck &&
    transition === 'keep_active' &&
    String(currentPstatus || '').toLowerCase() === 'hot';

  const handleChange = (id, value) => {
    const allFields = [...(schema?.mandatory ?? []), ...(schema?.optional ?? [])];
    const field = allFields.find((f) => f.id === id);
    const nextValue =
      field?.type === 'datetime' && typeof value === 'string'
        ? snapDatetimeLocalValue(value)
        : value;
    setAnswers((prev) => ({ ...prev, [id]: nextValue }));
    setValidationErrors([]);
  };

  const applyAllSuggestions = () => {
    if (!hints?.suggestions) return;
    setAnswers((prev) => mergeSuggestions(prev, hints.suggestions));
    toast.success('AI suggestions applied — review before confirming (summary is filled last)');
  };

  const handleBuildJustification = async () => {
    if (!leadId || !transition) return;
    try {
      setBuildingJustification(true);
      const draft = await buildStageQualificationJustificationDraft(leadId, transition, answers);
      if (!draft?.trim()) {
        toast.error('Complete the other questions first, then build the summary');
        return;
      }
      setAnswers((prev) => ({ ...prev, justification: draft }));
      toast.success('Summary built — review and edit before confirming');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not build summary');
    } finally {
      setBuildingJustification(false);
    }
  };

  const handleSubmit = async () => {
    if (showManagerAck && !managerAcknowledged) {
      toast.error('Confirm manager awareness before keeping a Hot lead active');
      return;
    }

    const payloadAnswers = { ...answers };
    if (showManagerAck && managerAcknowledged) {
      payloadAnswers.manager_acknowledged = true;
      payloadAnswers.manager_acknowledged_at = new Date().toISOString();
    }

    const payload = { transition, answers: payloadAnswers, leadId };
    try {
      await validateStageQualificationAnswers(payload);
      setValidationErrors([]);
      onSubmit?.(payload);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Please fix the highlighted issues';
      setValidationErrors(String(msg).split('; ').filter(Boolean));
      toast.error(msg);
    }
  };

  if (!open) return null;

  return (
    <Modal
      width={52}
      name={schema?.title || 'Stage qualification'}
      confirmOnClose
      isDirty
      onClose={onClose}>
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1 text-left w-full items-stretch">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <strong>{leadName}</strong>
          {targetStage ? (
            <span>
              {' '}
              — moving to <strong>{targetStage}</strong>. Mandatory fields are validated against
              CRM logs via Lead Intelligence AI.
            </span>
          ) : (
            <span> — complete mandatory qualification before proceeding.</span>
          )}
        </div>

        {hints?.intelligenceSummary ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
            <p className="font-medium text-slate-900">AI lead read ({hints.aiCategory} · {hints.aiScore}/100)</p>
            <p className="mt-1">{hints.intelligenceSummary}</p>
            {hints.suggestions && Object.keys(hints.suggestions).length > 0 ? (
              <button
                type="button"
                onClick={applyAllSuggestions}
                className="mt-2 text-xs font-medium text-blue-700 hover:underline">
                Apply all AI field suggestions
              </button>
            ) : null}
          </div>
        ) : null}

        {hints?.advisories?.length ? (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
            <p className="font-medium">AI checks vs CRM logs</p>
            <ul className="mt-1 list-disc pl-5">
              {hints.advisories.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {validationErrors.length ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">Fix before moving stage</p>
            <ul className="mt-1 list-disc pl-5">
              {validationErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {loading && <p className="text-sm text-gray-500">Loading form…</p>}

        {!loading &&
          visibleFields.map((field) => (
            <div
              key={field.id}
              className={`flex flex-col gap-1.5 w-full text-left items-stretch ${
                field.id === 'justification'
                  ? 'mt-2 border-t border-gray-200 pt-4'
                  : ''
              }`}>
              <label className="text-sm font-medium text-gray-800 text-left">
                {field.label}
                {field.required || field.id === 'budget_not_discussed_reason' ? (
                  <span className="text-red-500"> *</span>
                ) : null}
              </label>
              {field.helpText ? (
                <p className="text-xs text-gray-500 text-left">{field.helpText}</p>
              ) : null}
              <div className="w-full">
              <FieldInput
                field={field}
                value={answers[field.id]}
                otherValue={answers[`${field.id}_other`]}
                suggested={hints?.suggestions?.[field.id]}
                onChange={handleChange}
                onBuildJustification={
                  field.id === 'justification' ? handleBuildJustification : undefined
                }
                buildingJustification={buildingJustification}
              />
              </div>
            </div>
          ))}

        {!loading && optionalFields.length > 0 ? (
          <div className="rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setOptionalOpen((v) => !v)}
              className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-50">
              <span>Optional details ({optionalFields.length})</span>
              <span className="text-xs text-gray-500">{optionalOpen ? 'Hide' : 'Show'}</span>
            </button>
            {optionalOpen ? (
              <div className="flex flex-col gap-4 border-t border-gray-200 p-3">
                {optionalFields.map((field) => (
                  <div key={field.id} className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">{field.label}</label>
                    {field.helpText ? (
                      <p className="text-xs text-gray-500">{field.helpText}</p>
                    ) : null}
                    <FieldInput
                      field={{ ...field, required: false }}
                      value={answers[field.id]}
                      otherValue={answers[`${field.id}_other`]}
                      suggested={hints?.suggestions?.[field.id]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {showManagerAck ? (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
            <p className="font-medium">Manager awareness required</p>
            <p className="mt-1 text-xs opacity-90">
              This Hot lead is flagged for Nurture. Keeping it active will be logged for your sales
              manager to review.
            </p>
            {hints?.managerNote ? (
              <p className="mt-2 rounded bg-white/70 p-2 text-xs">{hints.managerNote}</p>
            ) : null}
            <label className="mt-3 flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={managerAcknowledged}
                onChange={(e) => setManagerAcknowledged(e.target.checked)}
              />
              <span className="text-xs">
                I have discussed this with my sales manager and we agree to keep this lead in the
                active pipeline.
              </span>
            </label>
          </div>
        ) : null}

        <div className="flex justify-end gap-3 border-t pt-4">
          <Button type="button" displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting || loading} onClick={handleSubmit}>
            {isSubmitting ? 'Saving…' : 'Confirm & move stage'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
